/**
 * Hooks del subdominio **Precios del cotizador** (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): la
 * red la gobierna `lib/api`. La query NO se segmenta por sede (precios globales,
 * el backend no resuelve sede aquí); la mutación, en éxito, **invalida** la query
 * para recargar el formulario (paridad con el "guardar + recargar" del SSR).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchPrecios,
  updatePrecios,
  type UpdatePreciosResult,
} from "../precios.api";
import type { PreciosConfig, UpdatePreciosBody } from "../precios.types";
import { PRECIOS_ROOT_KEY } from "./queryKeys";

/** Lee la configuración de precios del cotizador (`GET /cotizador/precios`). */
export function usePrecios() {
  return useQuery<PreciosConfig>({
    queryKey: PRECIOS_ROOT_KEY,
    queryFn: fetchPrecios,
    staleTime: 15_000,
  });
}

/**
 * Guarda la configuración de precios (`PUT /cotizador/precios`) y, en éxito,
 * invalida la query para recargar los valores persistidos.
 */
export function useUpdatePrecios() {
  const queryClient = useQueryClient();
  return useMutation<UpdatePreciosResult, Error, UpdatePreciosBody>({
    mutationFn: (body) => updatePrecios(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRECIOS_ROOT_KEY });
    },
  });
}
